import { useCallback, useEffect, useRef, useState } from 'react';

const INITIAL = {
  status: 'idle',
  completed: 0,
  total: 0,
  result: null,
  resultNoAbsences: null,
  scenario: null,
  durationMs: null,
  error: null,
};

let sharedState = INITIAL;
let sharedWorker = null;
const subscribers = new Set();

function notify(next) {
  sharedState = next;
  subscribers.forEach((setState) => setState(next));
}

function patchShared(patch) {
  notify({ ...sharedState, ...patch });
}

function attachWorkerHandlers(worker) {
  worker.onmessage = (e) => {
    const msg = e.data;
    if (msg.type === 'progress') {
      patchShared({
        completed: msg.completed,
        total: msg.total,
        scenario: msg.scenario,
      });
    } else if (msg.type === 'done') {
      patchShared({
        status: 'done',
        completed: msg.result.numSimulations,
        total: msg.result.numSimulations,
        result: msg.result,
        resultNoAbsences: msg.resultNoAbsences,
        scenario: null,
        durationMs: msg.durationMs,
      });
    } else if (msg.type === 'error') {
      patchShared({ status: 'error', error: msg.message });
    }
  };
}

export function useSimulation() {
  const workerRef = useRef(sharedWorker);
  const [state, setState] = useState(() => sharedState);

  useEffect(() => {
    subscribers.add(setState);
    setState(sharedState);
    workerRef.current = sharedWorker;

    if (sharedWorker && sharedState.status === 'running') {
      attachWorkerHandlers(sharedWorker);
    }

    return () => {
      subscribers.delete(setState);
    };
  }, []);

  const run = useCallback((numSimulations, seed, eloAdjustments, customAbsences) => {
    sharedWorker?.terminate();
    // Instantiate background Web Worker with Webpack 5 URL syntax
    const worker = new Worker(new URL('../lib/sim/worker.js', import.meta.url), { type: 'module' });
    sharedWorker = worker;
    workerRef.current = worker;

    notify({
      ...sharedState,
      status: 'running',
      completed: 0,
      total: numSimulations,
      scenario: 'withAbsences',
      durationMs: null,
      error: null,
    });

    attachWorkerHandlers(worker);
    worker.postMessage({ type: 'run', numSimulations, seed, eloAdjustments, customAbsences });
  }, []);

  const reset = useCallback(() => {
    sharedWorker?.terminate();
    sharedWorker = null;
    workerRef.current = null;
    notify(INITIAL);
  }, []);

  return { state, run, reset };
}
